import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Heart, Brain, Shield, Zap, Smile, Clock, Target, TrendingUp, FileText, Activity } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const HeartCoherencePage = () => {
  const benefits = [
    {
      system: "Système Nerveux",
      icon: Brain,
      effects: [
        "Équilibre du système nerveux autonome",
        "Réduction de l'activité sympathique (stress)",
        "Augmentation de l'activité parasympathique (relaxation)",
        "Amélioration de la clarté mentale et de la concentration"
      ],
      color: "from-sky-500 to-sky-600"
    },
    {
      system: "Système Cardiovasculaire",
      icon: Heart,
      effects: [
        "Amélioration de la variabilité de la fréquence cardiaque (VFC)",
        "Réduction de la tension artérielle",
        "Optimisation du débit cardiaque",
        "Protection contre les maladies cardiovasculaires"
      ],
      color: "from-sky-500 to-sky-600"
    },
    {
      system: "Système Immunitaire",
      icon: Shield,
      effects: [
        "Augmentation des IgA salivaires (première ligne de défense)",
        "Renforcement de l'immunité générale",
        "Réduction de l'inflammation chronique",
        "Meilleure résistance aux infections"
      ],
      color: "from-sky-500 to-sky-600"
    },
    {
      system: "Système Hormonal",
      icon: Zap,
      effects: [
        "Réduction du cortisol (hormone du stress)",
        "Augmentation de la DHEA (hormone de jeunesse)",
        "Amélioration du ratio cortisol/DHEA",
        "Régulation de l'insuline et de la glycémie"
      ],
      color: "from-sky-500 to-sky-600"
    },
    {
      system: "Bien-être Émotionnel",
      icon: Smile,
      effects: [
        "Réduction de l'anxiété et du stress perçu",
        "Amélioration de l'humeur et de la résilience émotionnelle",
        "Diminution des symptômes dépressifs",
        "Augmentation des émotions positives"
      ],
      color: "from-sky-500 to-sky-600"
    }
  ];

  const technique365 = [
    {
      number: "3",
      label: "fois par jour",
      description: "Pratiquez 3 séances quotidiennes : matin (au réveil), midi, et fin d'après-midi",
      icon: Clock
    },
    {
      number: "6",
      label: "respirations/min",
      description: "Respirez à une fréquence de 6 cycles par minute (5s inspiration + 5s expiration)",
      icon: Target
    },
    {
      number: "5",
      label: "minutes",
      description: "Chaque séance dure 5 minutes, soit 30 cycles respiratoires complets",
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Technique"
                title="La Cohérence"
                highlight="Cardiaque"
                subtitle="Une technique simple et scientifiquement prouvée pour harmoniser le cœur, le cerveau et les émotions."
                align="left"
              />
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_breathtrain/artifacts/1xsd106t_0d61b4b2-1f9e-4715-9393-f79dc29c5379.jpg"
                alt="Cohérence Cardiaque - Illustration"
                className="w-72 h-72 lg:w-96 lg:h-96 object-cover rounded-full shadow-2xl"
                style={{ border: '4px solid #E0F2FE' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Historique et Fondements Scientifiques
          </h2>
          
          <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
            <p>
              La cohérence cardiaque est une technique de gestion du stress et des émotions développée aux États-Unis dans les années 1990 par l'Institut HeartMath en Californie. Cette méthode s'appuie sur des décennies de recherches en neurosciences, cardiologie et psychophysiologie.
            </p>
            
            <p>
              Les scientifiques ont découvert que le <strong>cœur possède son propre système nerveux</strong>, souvent appelé "cerveau du cœur", composé d'environ 40 000 neurones. Ce réseau neuronal permet au cœur de prendre des décisions indépendantes et d'envoyer des informations au cerveau via le nerf vague.
            </p>

            <Card className="border-0 bg-sky-50 border-l-4 border-sky-500 my-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-sky-900 mb-3"> Découverte Clé</h3>
                <p className="text-sky-800">
                  Le cœur envoie plus d'informations au cerveau que le cerveau n'en envoie au cœur. 
                  En influençant notre rythme cardiaque par la respiration et les émotions, nous pouvons 
                  directement influencer notre cerveau et notre état émotionnel.
                </p>
              </CardContent>
            </Card>

            <p>
              La <strong>variabilité de la fréquence cardiaque (VFC)</strong> est l'indicateur central de la cohérence cardiaque. 
              Il s'agit de la variation naturelle du temps entre deux battements cardiaques. Contrairement à ce qu'on pourrait penser, 
              un cœur en bonne santé ne bat pas comme un métronome : les intervalles entre les battements varient constamment 
              en fonction de la respiration, des émotions, et de l'activité du système nerveux autonome.
            </p>
          </div>
        </div>
      </section>

      {/* Anatomy & Physiology */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Anatomie et Physiologie de la Cohérence Cardiaque
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-medium mb-3">Le Système Cardiaque</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>Nœud sinusal :</strong> Pacemaker naturel du cœur</li>
                  <li>• <strong>40 000 neurones :</strong> "Cerveau du cœur"</li>
                  <li>• <strong>Champ électromagnétique :</strong> 5000x plus puissant que celui du cerveau</li>
                  <li>• <strong>Production hormonale :</strong> ANF (facteur natriurétique auriculaire)</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-medium mb-3">La Communication Cœur-Cerveau</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• <strong>Nerf vague :</strong> Autoroute de communication bidirectionnelle</li>
                  <li>• <strong>80% des fibres :</strong> Remontent du cœur vers le cerveau</li>
                  <li>• <strong>Influence sur l'amygdale :</strong> Centre de gestion des émotions</li>
                  <li>• <strong>Modulation corticale :</strong> Impact sur la prise de décision</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 bg-sky-50 border-l-4 border-sky-500">
            <CardContent className="p-6">
              <h3 className="font-medium text-slate-800 mb-3">Le Mécanisme de la Cohérence</h3>
              <div className="text-slate-600 space-y-3 text-sm">
                <p>
                  <strong>1. Respiration à 6 cycles/minute :</strong> Cette fréquence spécifique correspond à la fréquence de résonance du système cardiovasculaire, créant une synchronisation optimale entre le cœur, la respiration et la tension artérielle.
                </p>
                <p>
                  <strong>2. Onde sinusoïdale régulière :</strong> En état de cohérence, la VFC forme une courbe harmonieuse et régulière, signe d'un équilibre parfait entre les systèmes sympathique et parasympathique.
                </p>
                <p>
                  <strong>3. Synchronisation globale :</strong> Le cœur, le cerveau, la respiration et la tension artérielle entrent en résonance, créant un état d'harmonie physiologique optimal.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* HRV Comparison Images */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Stress State */}
            <div className="text-center">
              <div className="rounded-xl overflow-hidden shadow-lg bg-white p-4">
                <img 
                  src="/images/hrv-stress.png" 
                  alt="État de stress - VFC chaotique de faible amplitude" 
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <p className="mt-4 text-slate-600 text-sm">
                <strong className="text-slate-800">État de stress</strong> : Variabilité de fréquence cardiaque (VFC) 
                chaotique et de faible amplitude
              </p>
            </div>
            
            {/* Coherence State */}
            <div className="text-center">
              <div className="rounded-xl overflow-hidden shadow-lg bg-white p-4">
                <img 
                  src="/images/hrv-coherence.png" 
                  alt="État de cohérence cardiaque - VFC harmonieuse de grande amplitude" 
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <p className="mt-4 text-slate-600 text-sm">
                <strong className="text-slate-800">État de cohérence cardiaque</strong> : VFC harmonieuse 
                avec une amplitude beaucoup plus grande
              </p>
            </div>
          </div>

          {/* Detailed VFC Explanation */}
          <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              Comprendre la Variabilité de la Fréquence Cardiaque (VFC)
            </h3>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              La variabilité de la fréquence cardiaque (VFC) mesure les fluctuations du temps entre les battements cardiaques, 
              reflétant l'équilibre du système nerveux autonome (SNA), entre le sympathique (« combat ou fuite ») et le 
              parasympathique (« repos et digestion »). Une VFC élevée est souvent un signe de bonne santé, d'adaptation et 
              de récupération, tandis qu'une faible VFC peut indiquer stress, fatigue ou problèmes cardiovasculaires. 
              Mesurée par des appareils connectés, elle est un indicateur de performance physique, de récupération et de 
              gestion du stress, bien plus utile dans son évolution personnelle que par comparaison avec autrui.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-sky-600 mb-3">Qu'est-ce que c'est ?</h4>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-sky-500 mt-1">•</span>
                    <span>Le cœur ne bat pas à un rythme parfaitement régulier ; la VFC est la mesure de ces variations en millisecondes.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sky-500 mt-1">•</span>
                    <span>C'est un indicateur de la capacité d'adaptation du corps aux changements et aux sollicitations.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold text-sky-600 mb-3">Ce qu'elle révèle</h4>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-sky-500 mt-1">•</span>
                    <span><strong>Système nerveux autonome (SNA) :</strong> Elle montre l'équilibre entre le système sympathique (excitateur) et parasympathique (freinateur).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sky-500 mt-1">•</span>
                    <span><strong>Santé et récupération :</strong> Une VFC élevée signifie une bonne santé cardiovasculaire et une bonne capacité d'adaptation.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sky-500 mt-1">•</span>
                    <span><strong>Stress et effort :</strong> Une VFC basse peut indiquer un stress élevé, un mauvais sommeil, une fatigue ou une maladie.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits by System */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Effets physiologiques de la pratique de la cohérence cardiaque sur l'organisme et le mental
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <div className={`h-2 bg-gradient-to-r ${benefit.color}`} />
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center`}>
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-medium">{benefit.system}</h3>
                  </div>
                  <ul className="space-y-2">
                    {benefit.effects.map((effect, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-sky-500 mt-1">✓</span>
                        <span>{effect}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-0 bg-gradient-to-r from-sky-500 to-sky-600 text-white mt-8">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Effets Mesurables Dès 3 Semaines</h3>
              <p className="text-sky-100 max-w-2xl mx-auto">
                Les études montrent des améliorations significatives de la VFC, une réduction du cortisol de 23%, 
                et une augmentation des IgA de 50% après seulement 3 semaines de pratique régulière (3x5 minutes/jour).
              </p>
            </CardContent>
          </Card>

          {/* Link to Documents */}
          <div className="mt-8 text-center">
            <Link 
              to="/documents" 
              className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-medium text-lg transition-colors"
            >
              <FileText size={20} />
              Pour en savoir plus, consultez les Documents du site
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Infographic Summary */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Infographie récapitulative
          </h2>
          <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
            La méthode 3.6.5 et ses bénéfices physiologiques et mentaux d&apos;un seul coup d&apos;œil.
          </p>
          <div className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-slate-200">
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/sp9g7ks8_ChatGPT%20Image%2022%20juil.%202026%2C%2007_42_26.png"
              alt="Infographie Cohérence Cardiaque 3.6.5 — bénéfices physiologiques et mentaux"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="coherence-infographic"
            />
          </div>
        </div>
      </section>

      {/* 365 Technique */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            La Méthode 365 : Comment Pratiquer
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Une formule simple et efficace développée par le Dr David Servan-Schreiber pour intégrer 
            la cohérence cardiaque dans votre quotidien.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {technique365.map((item, index) => (
              <Card key={index} className="border-0 shadow-xl text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-6xl font-bold text-sky-600 mb-2">{item.number}</div>
                  <div className="text-slate-600 font-semibold mb-3">{item.label}</div>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Practice Steps */}
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-medium mb-6 text-center">
                Guide de Pratique Étape par Étape
              </h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Installation</h4>
                    <p className="text-slate-600 text-sm">
                      Asseyez-vous confortablement, dos droit mais détendu. Posez vos pieds à plat sur le sol 
                      et vos mains sur vos cuisses ou votre abdomen. Fermez les yeux si vous le souhaitez.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Respiration à 6 cycles/minute</h4>
                    <p className="text-slate-600 text-sm mb-2">
                      Respirez profondément et lentement :
                    </p>
                    <ul className="text-slate-600 text-sm space-y-1 ml-4">
                      <li>• <strong>Inspiration :</strong> 5 secondes (comptez mentalement 1-2-3-4-5)</li>
                      <li>• <strong>Expiration :</strong> 5 secondes (comptez mentalement 1-2-3-4-5)</li>
                      <li>• Respirez par le nez de préférence, de façon fluide et naturelle</li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">
                      Focalisez votre Attention sur la Zone du Cœur
                    </h4>
                    <p className="text-slate-600 text-sm">
                      Portez votre attention sur la région de votre cœur, au centre de votre poitrine. 
                      Imaginez que vous respirez à travers votre cœur, comme si l'air entrait et sortait 
                      directement par cette zone.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">
                       Générez une Émotion Positive (Aspect Clé)
                    </h4>
                    <p className="text-slate-600 text-sm mb-2">
                      Tout en maintenant votre respiration à 6 cycles/minute, <strong>évoquez un sentiment 
                      ou un souvenir positif</strong> :
                    </p>
                    <ul className="text-slate-600 text-sm space-y-1 ml-4">
                      <li>• Un moment de gratitude pour quelqu'un ou quelque chose</li>
                      <li>• Un souvenir heureux avec un être cher</li>
                      <li>• Un lieu apaisant (plage, forêt, montagne)</li>
                      <li>• Un sentiment d'amour, de compassion ou d'appréciation</li>
                    </ul>
                    <div className="mt-3 p-3 rounded-lg border-l-2 border-sky-400">
                      <p className="text-slate-800 text-sm">
                        <strong> Pourquoi c'est crucial :</strong> Les émotions positives amplifient considérablement 
                        l'effet de la cohérence cardiaque. Elles créent un pattern cardiaque encore plus cohérent 
                        et renforcent la synchronisation cœur-cerveau, multipliant les bienfaits de la pratique.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">5</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Maintenir pendant 5 Minutes</h4>
                    <p className="text-slate-600 text-sm">
                      Continuez cette respiration rythmée tout en maintenant votre attention sur le cœur 
                      et l'émotion positive. Laissez votre esprit s'apaiser. Si des pensées surgissent, 
                      ramenez doucement votre attention sur votre respiration et votre cœur.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Best Times */}
          <Card className="border-0 bg-gradient-to-r from-sky-500 to-sky-600 text-white mt-8">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold mb-4 text-center">⏰ Moments Optimaux pour Pratiquer</h3>
              <div className="grid md:grid-cols-3 gap-6 text-sky-100">
                <div className="text-center">
                  <div className="text-3xl mb-2">🌅</div>
                  <div className="font-semibold mb-1">Matin</div>
                  <div className="text-sm">Au réveil, pour préparer la journée avec calme et clarté</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">☀️</div>
                  <div className="font-semibold mb-1">Midi</div>
                  <div className="text-sm">Avant le déjeuner, pour digérer le stress de la matinée</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🌆</div>
                  <div className="font-semibold mb-1">Fin d'après-midi</div>
                  <div className="text-sm">16h-17h, pour prévenir le pic de cortisol du soir</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Biofeedback Section */}
          <Card 
            className="border-0 rounded-3xl text-white mt-8 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #4A6D8C 0%, #334F6B 100%)' }}
          >
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Activity className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Pratique avec Biofeedback</h3>
              <p className="text-white/85 mb-6 max-w-2xl mx-auto">
                Le biofeedback permet de visualiser en temps réel votre état de cohérence cardiaque. 
                Découvrez comment utiliser cette technologie pour optimiser votre pratique.
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_53d1ac5b-bd6d-4cf4-9a95-23a968bb12a6/artifacts/fcpxyp5e_coherence_cardiaque_biofeedback.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="biofeedback-pdf-button"
                className="inline-block px-8 py-4 bg-white text-sky-600 font-bold rounded-full hover:bg-sky-50 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📄 Consulter le guide Biofeedback
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Scientific Evidence */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Validations Scientifiques
          </h2>
          
          <div className="space-y-4 text-slate-600">
            <p>
              Plus de 300 études scientifiques ont été publiées sur la cohérence cardiaque et la variabilité 
              de la fréquence cardiaque. Les recherches montrent des effets mesurables et reproductibles dans 
              de nombreux domaines :
            </p>

            <Card className="border-0">
              <CardContent className="p-6">
                <ul className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Réduction du stress et de l'anxiété :</strong> Diminution de 23% du cortisol après 3 semaines</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Performance cognitive :</strong> Amélioration de la mémoire, de l'attention et de la prise de décision</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Résilience émotionnelle :</strong> Meilleure gestion des émotions difficiles et des situations stressantes</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Santé cardiovasculaire :</strong> Réduction de la tension artérielle et du risque cardiovasculaire</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Immunité :</strong> Augmentation de 50% des IgA salivaires</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-sky-500 font-bold">✓</span>
                    <span><strong>Sommeil :</strong> Amélioration de la qualité du sommeil et réduction de l'insomnie</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Commencez Votre Pratique de Cohérence Cardiaque
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Découvrez nos exercices guidés de cohérence cardiaque et d'autres techniques de respiration 
            pour améliorer votre santé et votre bien-être.
          </p>
          <Link to="/app-landing">
            <button className="px-10 py-4 text-lg rounded-full bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg font-semibold transition-colors">
              Accéder aux exercices guidés
            </button>
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default HeartCoherencePage;
